import { CloseIcon, PlusIcon } from '@/assets/icons'
import { iconSizes } from '@/theme/tokens'
import {
	ATTACHMENT_ACCEPT,
	DOCUMENT_ACCEPT,
	IMAGE_ACCEPT,
	PendingThumbnails,
	overCapMessage,
} from '@components/Attachments'
import { Button } from '@components/Button'
import { FileInput, Textarea } from '@components/Field'
import React, { useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { LinkInput, LinkInputWrapper } from '../AddEventModal/FormComponents.styles'
import {
	EventCreateDraft,
	MAX_EVENT_LINKS,
	addFiles,
	addLink,
	canAddFile,
	canAddFiles,
	canAddLink,
	removeFile,
	remainingFiles,
	removeLink,
	removeSavedFile,
	setLink,
	setNotes,
} from './core/draft'
import { ATTACHMENTS_SUBTITLE, LINKS_SUBTITLE, NOTES_SUBTITLE } from './core/presentation'
import { AddLink, AddRow, Divider, LinkRows, StepForm } from './EventCreateFlow.styles'
import ExtrasSection from './ExtrasSection'

interface ExtrasStepProps {
	draft: EventCreateDraft
	onChange: (draft: EventCreateDraft) => void
}

/**
 * Step 3 — Extras: photos and documents (a saved event's own first, when
 * editing), links and notes, each an optional section with a plus, split by
 * dividers. Once a section has something in it
 * the old form's controls take over from the plus: the attachment grid with
 * "Image" / "Document", the link fields with "Add Link", and for notes a ✕
 * that clears and closes them. Nothing here is required.
 */
const ExtrasStep: React.FC<ExtrasStepProps> = ({ draft, onChange }) => {
	const anyFileInput = useRef<HTMLInputElement>(null)
	const imageInput = useRef<HTMLInputElement>(null)
	const documentInput = useRef<HTMLInputElement>(null)
	const linkRows = useRef<HTMLDivElement>(null)
	const notesField = useRef<HTMLTextAreaElement>(null)

	/** Opened with its plus while still empty. */
	const [notesOpened, setNotesOpened] = useState(false)
	/** What to focus once the render that adds it lands. */
	const [focusNext, setFocusNext] = useState<'lastLink' | 'notes' | null>(null)

	useEffect(() => {
		if (focusNext === 'lastLink') Array.from(linkRows.current?.querySelectorAll('input') ?? []).at(-1)?.focus()
		if (focusNext === 'notes') notesField.current?.focus()
		if (focusNext) setFocusNext(null)
	}, [focusNext])

	const pickFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
		const picked = Array.from(e.target.files ?? [])
		e.target.value = ''
		if (picked.length === 0) return
		if (!canAddFiles(draft, picked.length)) {
			toast.error(overCapMessage(remainingFiles(draft)))
			return
		}
		onChange(addFiles(draft, picked))
	}

	// As the old form: a new empty field under the last, up to the cap.
	const addLinkRow = () => {
		if (!canAddLink(draft)) {
			toast.error(`You can only add up to ${MAX_EVENT_LINKS} links.`)
			return
		}
		onChange(addLink(draft))
		setFocusNext('lastLink')
	}

	return (
		<StepForm>
			<ExtrasSection
				title="Photos & Documents"
				subtitle={ATTACHMENTS_SUBTITLE}
				expanded={draft.savedFiles.length > 0 || draft.files.length > 0}
				onAdd={() => anyFileInput.current?.click()}
			>
				<PendingThumbnails
					files={draft.files}
					onRemove={index => onChange(removeFile(draft, index))}
					savedFiles={draft.savedFiles}
					onRemoveSaved={fileId => onChange(removeSavedFile(draft, fileId))}
				/>
				{canAddFile(draft) && (
					<AddRow>
						<Button type="button" variant="secondary" size="sm" onClick={() => imageInput.current?.click()}>
							<PlusIcon sx={{ fontSize: iconSizes.sm }} />
							Image
						</Button>
						<Button type="button" variant="secondary" size="sm" onClick={() => documentInput.current?.click()}>
							<PlusIcon sx={{ fontSize: iconSizes.sm }} />
							Document
						</Button>
					</AddRow>
				)}
			</ExtrasSection>
			{/* The section's plus offers every kind at once; the body's buttons one each. */}
			<FileInput ref={anyFileInput} multiple accept={ATTACHMENT_ACCEPT} onChange={pickFiles} aria-label="Add photos or documents" />
			<FileInput ref={imageInput} multiple accept={IMAGE_ACCEPT} onChange={pickFiles} aria-label="Add images" />
			<FileInput ref={documentInput} multiple accept={DOCUMENT_ACCEPT} onChange={pickFiles} aria-label="Add documents" />

			<Divider />

			<ExtrasSection title="URL Links" subtitle={LINKS_SUBTITLE} expanded={draft.links.length > 0} onAdd={addLinkRow}>
				<LinkRows ref={linkRows}>
					{draft.links.map((link, index) => (
						<LinkInputWrapper key={index}>
							<LinkInput
								type="text"
								inputMode="url"
								aria-label={`Link ${index + 1}`}
								placeholder="Enter link"
								value={link}
								onChange={e => onChange(setLink(draft, index, e.target.value))}
								autoComplete="off"
							/>
							<Button type="button" variant="icon" aria-label="Remove link" onClick={() => onChange(removeLink(draft, index))}>
								<CloseIcon sx={{ fontSize: iconSizes.md }} />
							</Button>
						</LinkInputWrapper>
					))}
				</LinkRows>
				<AddLink type="button" onClick={addLinkRow}>
					<PlusIcon sx={{ fontSize: iconSizes.sm }} />
					Add Link
				</AddLink>
			</ExtrasSection>

			<Divider />

			<ExtrasSection
				title="Notes"
				subtitle={NOTES_SUBTITLE}
				expanded={notesOpened || draft.notes !== ''}
				removable
				onAdd={() => {
					setNotesOpened(true)
					setFocusNext('notes')
				}}
				onRemove={() => {
					setNotesOpened(false)
					onChange(setNotes(draft, ''))
				}}
			>
				<Textarea
					ref={notesField}
					aria-label="Notes"
					placeholder="Add notes"
					value={draft.notes}
					onChange={e => onChange(setNotes(draft, e.target.value))}
					rows={5}
				/>
			</ExtrasSection>
		</StepForm>
	)
}

export default ExtrasStep
